package fr.manyverse.app.presentation.components

import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.Spacer
import androidx.compose.foundation.layout.fillMaxSize
import androidx.compose.foundation.layout.height
import androidx.compose.foundation.layout.padding
import androidx.compose.material3.Button
import androidx.compose.material3.MaterialTheme
import androidx.compose.material3.Text
import androidx.compose.material3.TextButton
import androidx.compose.runtime.Composable
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.platform.testTag
import androidx.compose.ui.text.style.TextAlign
import androidx.compose.ui.unit.dp
import manyverse.shared.generated.resources.Res
import manyverse.shared.generated.resources.back
import manyverse.shared.generated.resources.error_generic
import manyverse.shared.generated.resources.retry
import org.jetbrains.compose.resources.stringResource

/**
 * Reusable error state for `AsyncUiState.Error` branches.
 *
 * Displays an optional title, the error message, and a retry button.
 * When [message] is blank, a localized generic message is shown instead.
 *
 * @param message Error message coming from `AsyncUiState.Error.message`. May be blank.
 * @param onRetry Invoked when the user taps the retry button. When null, no button is shown.
 * @param title Optional localized title displayed above the message.
 * @param onBack Shows a back button below the retry one. Required on a full-screen error without
 *   a top bar: iOS, web and desktop have no system back button to leave it.
 */
@Composable
fun ErrorState(
    message: String,
    onRetry: (() -> Unit)?,
    modifier: Modifier = Modifier,
    title: String? = null,
    onBack: (() -> Unit)? = null
) {
    val displayMessage = message.ifBlank { stringResource(Res.string.error_generic) }

    Column(
        modifier = modifier
            .fillMaxSize()
            .padding(16.dp)
            .testTag("errorStateColumn"),
        horizontalAlignment = Alignment.CenterHorizontally,
        verticalArrangement = Arrangement.Center
    ) {
        if (title != null) {
            Text(
                text = title,
                style = MaterialTheme.typography.titleMedium,
                color = MaterialTheme.colorScheme.error,
                textAlign = TextAlign.Center,
                modifier = Modifier.testTag("errorTitleText")
            )
            Spacer(modifier = Modifier.height(8.dp))
        }

        Text(
            text = displayMessage,
            style = MaterialTheme.typography.bodyMedium,
            color = MaterialTheme.colorScheme.onSurfaceVariant,
            textAlign = TextAlign.Center,
            modifier = Modifier.testTag("errorMessageText")
        )

        if (onRetry != null) {
            Spacer(modifier = Modifier.height(16.dp))
            Button(
                onClick = onRetry,
                modifier = Modifier.testTag("retryButton")
            ) {
                Text(stringResource(Res.string.retry))
            }
        }

        if (onBack != null) {
            Spacer(modifier = Modifier.height(8.dp))
            TextButton(
                onClick = onBack,
                modifier = Modifier.testTag("backButton")
            ) {
                Text(stringResource(Res.string.back))
            }
        }
    }
}
